import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "./firebase";
import PatientDashboard from "./PatientDashboard";
import ProviderDashboard from "./ProviderDashboard";
import EmployeeDashboard from "./EmployeeDashboard";
import { useLanguage } from "./LanguageContext";

function Dashboard() {
  const [userData, setUserData] = useState(null);
  const [providerData, setProviderData] = useState(null);
  const [employeeData, setEmployeeData] = useState(null);
  const navigate = useNavigate();
  const { lang, toggleLang, t } = useLanguage();

  const loadData = async () => {
    const user = auth.currentUser;
    if (!user) {
      navigate("/login");
      return;
    }
    const snap = await getDoc(doc(db, "users", user.uid));
    if (snap.exists()) {
      setUserData(snap.data());
      if (snap.data().role === "employee") {
        const empSnap = await getDoc(doc(db, "employees", user.uid));
        if (empSnap.exists()) setEmployeeData(empSnap.data());
      } else if (snap.data().role !== "patient") {
        const provSnap = await getDoc(doc(db, "providers", user.uid));
        if (provSnap.exists()) setProviderData(provSnap.data());
      }
    }
  };

  useEffect(() => {
    const unsub = auth.onAuthStateChanged((user) => {
      if (!user) {
        navigate("/login");
        return;
      }
      loadData();
    });
    return () => unsub();
  }, [navigate]);

  if (!userData) return <p style={{ textAlign: "center", marginTop: 60 }}>{t("loading")}</p>;

  if (userData.role === "patient") {
    return <PatientDashboard userData={userData} />;
  }

  if (userData.role === "employee") {
    if (!employeeData) return <p style={{ textAlign: "center", marginTop: 60 }}>{t("loading")}</p>;
    return <EmployeeDashboard userData={userData} employeeData={employeeData} onUpdate={loadData} />;
  }

  if (providerData) {
    return <ProviderDashboard userData={userData} providerData={providerData} onProviderUpdate={loadData} />;
  }

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <button onClick={toggleLang} style={styles.langToggle}>
          {lang === "de" ? "EN" : "DE"}
        </button>
        <h1 style={styles.brand}>{t("appName")}</h1>
        <h2 style={{ marginTop: 20 }}>{t("welcome")}, {userData.name}</h2>
        <p style={styles.meta}>{lang === "de" ? "Rolle" : "Role"}: {userData.role}</p>

        <div style={styles.notice}>
          <p>{t("completeProfilePrompt")}</p>
          <button onClick={() => navigate("/profile-setup")} style={styles.button}>
            {lang === "de" ? "Profil ausfullen" : "Fill out profile"}
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(135deg, #eef5ff 0%, #f7fbff 100%)",
    fontFamily: "'Segoe UI', sans-serif",
    padding: 20,
  },
  card: {
    background: "#ffffff",
    padding: "40px 36px",
    borderRadius: 16,
    boxShadow: "0 8px 30px rgba(0,0,0,0.08)",
    width: "100%",
    maxWidth: 460,
    position: "relative",
  },
  langToggle: {
    position: "absolute",
    top: 16,
    right: 16,
    padding: "4px 10px",
    borderRadius: 6,
    border: "1px solid #ccc",
    background: "#f5f5f5",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
    color: "#333",
  },
  brand: { margin: 0, fontSize: 22, color: "#1a5fb4", textAlign: "center" },
  meta: { color: "#555", fontSize: 14 },
  notice: {
    background: "#f0f6ff",
    padding: 16,
    borderRadius: 10,
    marginTop: 16,
  },
  button: {
    padding: "10px 18px",
    background: "#1a5fb4",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    marginTop: 10,
  },
};

export default Dashboard;
